<template>
  <div v-show="visible" class="tab-pane-content">
    <!-- Table -->
    <data-table
      ref="dataTableRef"
      :config="tableConfig"
      :endpoint="endpoint"
      :auto-load="false"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="(e: any) => handleGridRowClick(e)"
      @edit-click="handleGridEditClick"
      @button-click="handleGridButtonClick"
    />
  </div>
</template>

<script setup lang="ts">
import { whatsappNumber } from '../../composables/usePhoneFormat';
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  data: Record<string, unknown>;
  value?: unknown;
}

interface TabProps {
  visible?: boolean;
}

const props = withDefaults(defineProps<TabProps>(), {
  visible: false
});

const emit = defineEmits<{
  'update:total': [total: number];
}>();

const { t } = useI18n();

const dataTableRef = ref<DataTableInstance | null>(null);

// Endpoint
const endpoint = computed(() => '/index.php/gestao_clientes/carrega_orcamentos_com_compromisso');

// DataTable config
const tableConfig = computed(() => {
  return {
    tableId: 'tabelaOrcamentosComCompromisso',
    title: t('Orçamentos com Compromisso'),
    filename: `orcamentos_com_compromisso_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pagination: true,
    pageLength: 25,
    heightMode: 'fill-viewport',
    rowSelection: 'single'
  };
});

const filterConfig = computed(() => {
  return {
    columns: {
      'ID': 'text',
      'orcamento_data': 'daterange',
      'status_label': 'select',
      'franquia': 'select',
      'nome_cliente': 'text',
      'rua': 'text',
      'bairro': 'text',
      'Localidade': 'text',
      'total': 'range',
      'cliente_telefone': 'text',
      'origem': 'select'
    }
  };
});

const refreshButtonConfig = computed(() => ({ show: true, text: t('Atualizar') }));
const csvButtonConfig = computed(() => ({ show: true, text: 'CSV', loading: false }));

const tableColumns = computed(() => {
  return [
    {
      data: 'ID',
      title: 'ID',
      width: 80,
      resizable: true,
      sortable: true,
      renderType: 'custom_clickable_id',
      clickCallback: (value: Id | undefined) => {
        if (value) eventBus.emit('orcamento.edit', value);
      },
      dataType: 'number',
    },
    {
      data: 'status_label',
      title: t('Status', 'Status'),
      width: 200,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams) => {
        const row = (params.data || {}) as { status?: string };
        const status = row.status;
        let severity = 'secondary';
        let icone = 'info-circle';
        switch (status) {
          case 'Pendente': severity = 'warn'; icone = 'exclamation-triangle'; break;
          case 'Respondido': severity = 'info'; icone = 'clock'; break;
          case 'Aprovado': severity = 'success'; icone = 'check'; break;
          case 'Parcialmente concluído': severity = 'info'; icone = 'check'; break;
          case 'Todos os serviços realizados': severity = 'primary'; icone = 'flag'; break;
          case 'Cancelado': severity = 'secondary'; icone = 'times'; break;
        }
        const span = document.createElement('span');
        span.className = `status-badge status-${severity}`;
        span.innerHTML = `<i class="pi pi-${icone}"></i> ${params.value || ''}`;
        return span;
      }
    },
    {
      data: 'orcamento_data',
      title: t('Data', 'Data'),
      width: 160,
      resizable: true,
      sortable: true,
      renderType: 'datetime',
      dataType: 'date',
    },
    { 
      data: 'franquia', 
      title: t('Franquia', 'Franquia'), 
      width: 150,
      resizable: true,
      sortable: true,
    },
    {
      data: 'nome_cliente',
      title: t('Cliente', 'Cliente'),
      width: 220,
      resizable: true,
      sortable: true,
      renderType: 'custom_clickable_id',
      clickCallback: (_value: unknown, row: { cliente_id?: Id } | undefined) => {
        if (row?.cliente_id) {
          eventBus.emit('cliente.edit', row.cliente_id);
        }
      },
    },
    {
      data: 'cliente_telefone',
      title: 'WhatsApp',
      width: 180,
      resizable: true,
      cellRenderer: (params: CellParams) => {
        const row = (params.data || {}) as { cliente_telefone?: string; cliente_ddd?: string; cliente_ddi?: string };
        // O número completo vem de uma regra só (usePhoneFormat): país sem DDD também ganha botão.
        const numero = whatsappNumber(row);
        if (numero === null) return '';
        const a = document.createElement('a');
        a.className = 'p-button p-button-success p-button-sm btn-whatsapp';
        a.href = `https://api.whatsapp.com/send?phone=${numero}`;
        a.target = '_blank';
        a.title = 'WhatsApp';
        a.innerHTML = `<i class="pi pi-whatsapp" aria-hidden="true"></i> +${numero}`;
        return a;
      }
    },
    { 
      data: 'rua', 
      title: t('Rua', 'Rua'), 
      width: 220,
      resizable: true,
      sortable: true,
    },
    { 
      data: 'bairro', 
      title: t('Bairro', 'Bairro'), 
      width: 140,
      resizable: true,
      sortable: true,
    },
    { 
      data: 'Localidade', 
      title: t('Local', 'Local'), 
      width: 140,
      resizable: true,
      sortable: true,
    },
    { 
      data: 'total', 
      title: t('Total', 'Total'), 
      width: 120,
      resizable: true,
      sortable: true,
      renderType: 'currency',
      dataType: 'number',
    },
    { 
      data: 'origem', 
      title: t('Origem', 'Origem'), 
      width: 140,
      resizable: true,
      sortable: true,
    }
  ];
});

const loadData = (): void => {
  log('GestaoClientesTabOrcamentosComCompromisso', 'loadData', 'Triggering grid refresh');
  dataTableRef.value?.loadData();
};

const handleDataLoaded = (data: unknown[]): void => {
  log('GestaoClientesTabOrcamentosComCompromisso', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  emit('update:total', data.length || 0);
};

const handleDataError = (err: { message?: string } | Error): void => {
  log('GestaoClientesTabOrcamentosComCompromisso', 'handleDataError', 'Error loading data', err);
  const message = err instanceof Error ? err.message : err.message || '';
  eventBus.emit('globalMessage.error', t('Erro ao carregar orçamentos com compromisso: ') + message);
};

const handleGridRowClick = (rowData?: { ID?: Id }): void => {
  log('GestaoClientesTabOrcamentosComCompromisso', 'handleGridRowClick', 'Row clicked', rowData?.ID || null);
  if (rowData?.ID) handleEditarOrcamento(rowData.ID);
};

const handleGridEditClick = (orcamentoId: Id): void => {
  log('GestaoClientesTabOrcamentosComCompromisso', 'handleGridEditClick', 'Edit clicked from grid', orcamentoId);
  handleEditarOrcamento(orcamentoId);
};

interface GridButtonClickPayload {
  value?: Id;
  data?: { cliente_id?: Id };
  action: string;
}

const handleGridButtonClick = ({ value, data, action }: GridButtonClickPayload): void => {
  log('GestaoClientesTabOrcamentosComCompromisso', 'handleGridButtonClick', 'Button clicked', { value, data, action });
  if (action === 'editar-cliente') {
    const clienteId = data?.cliente_id;
    if (clienteId) eventBus.emit('cliente.edit', clienteId);
  }
};

const handleEditarOrcamento = (orcamentoId: Id): void => {
  log('GestaoClientesTabOrcamentosComCompromisso', 'handleEditarOrcamento', 'Opening edit orcamento modal', orcamentoId);
  if (orcamentoId) eventBus.emit('orcamento.edit', orcamentoId);
};

const handleOrcamentoSaved = (): void => {
  if (props.visible) loadData();
};

const handleClienteSaved = (): void => {
  if (props.visible) loadData();
};

const handleCompromissoSaved = (): void => {
  if (props.visible) loadData();
};

onMounted(() => {
  log('GestaoClientesTabOrcamentosComCompromisso', 'onMounted', 'Component mounted');

  eventBus.on('orcamento.saved', handleOrcamentoSaved);
  eventBus.on('cliente.saved', handleClienteSaved);
  eventBus.on('compromisso.saved', handleCompromissoSaved);
});

onBeforeUnmount(() => {
  log('GestaoClientesTabOrcamentosComCompromisso', 'onBeforeUnmount', 'Cleaning up listeners');
  eventBus.off('orcamento.saved', handleOrcamentoSaved);
  eventBus.off('cliente.saved', handleClienteSaved);
  eventBus.off('compromisso.saved', handleCompromissoSaved);
});

defineExpose({
  loadData
});
</script>

<style scoped>
.tab-pane-content {
  padding: 20px;
}

.btn-whatsapp {
  white-space: nowrap;
}
</style>

